import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { Download, Mail, Package, Ban, Loader2, RefreshCw, ChevronDown, ChevronUp, History } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/digital")({
  component: DigitalAdminPage,
  head: () => ({ meta: [{ title: "Productos digitales — Structura Market" }] }),
});

type GrantRow = {
  id: string;
  orderId: string;
  orderNumber: string | null;
  customerEmail: string;
  status: string;
  downloadCount: number;
  downloadLimit: number | null;
  expiresAt: string | null;
  asset?: { displayName?: string };
};

type EventRow = {
  event_type: string;
  created_at: string;
  meta?: Record<string, unknown>;
};

function DigitalAdminPage() {
  const tenantId = useWorkspaceTenantId();
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const d = await apiGetJson<{ grants: GrantRow[] }>("/api/digital-deliveries");
      setGrants(d.grants ?? []);
    } catch {
      toast.error("No se pudieron cargar las entregas");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadEvents = async (grantId: string) => {
    if (expandedId === grantId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(grantId);
    setEventsLoading(true);
    try {
      const d = await apiGetJson<{ events: EventRow[] }>(
        `/api/digital-deliveries?grantId=${encodeURIComponent(grantId)}`,
      );
      setEvents(d.events ?? []);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const act = async (grantId: string, action: "revoke" | "resend" | "regenerate") => {
    setBusyId(grantId);
    try {
      await apiSendJson("/api/digital-deliveries", "POST", { action, grantId });
      toast.success(
        action === "revoke"
          ? "Acceso revocado"
          : action === "regenerate"
            ? "Link regenerado y email enviado"
            : "Email reenviado",
      );
      await load();
      if (expandedId === grantId) void loadEvents(grantId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <DashboardHeader title="Productos digitales" subtitle="Entregas, descargas, auditoría y recovery" />
      <ClinicalPageShell offset="fixed-title">
        <MerchantExperienceBar screenId="digital" />
        <div className="mb-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/productos">
            <Package className="mr-2 h-4 w-4" /> Gestionar productos y assets
          </Link>
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando entregas…
        </div>
      ) : grants.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
          Sin entregas digitales todavía. Creá un producto tipo digital en Catalogación y completá una venta.
        </p>
      ) : (
        <div className="space-y-3">
          {grants.map((g) => (
            <div key={g.id} className="rounded-xl border border-border/60 bg-card/50 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">
                    {g.asset?.displayName ?? "Archivo digital"}
                  </p>
                  <p className="text-sm text-muted-foreground">{g.customerEmail}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Pedido {g.orderNumber ?? g.orderId.slice(0, 8)}
                    {" · "}
                    {g.downloadCount}
                    {g.downloadLimit != null ? `/${g.downloadLimit}` : ""} descargas
                    {g.expiresAt
                      ? ` · vence ${format(parseISO(g.expiresAt), "d MMM yyyy", { locale: es })}`
                      : ""}
                  </p>
                  <span
                    className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      g.status === "active"
                        ? "bg-emerald-500/15 text-emerald-600"
                        : g.status === "expired"
                          ? "bg-amber-500/15 text-amber-700"
                          : g.status === "revoked"
                            ? "bg-red-500/15 text-red-600"
                            : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {g.status}
                  </span>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="ghost" onClick={() => void loadEvents(g.id)}>
                    <History className="mr-1.5 h-4 w-4" />
                    Auditoría
                    {expandedId === g.id ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />}
                  </Button>
                  {g.status === "active" ? (
                    <>
                      <Button size="sm" variant="outline" disabled={busyId === g.id} onClick={() => void act(g.id, "resend")}>
                        <Mail className="mr-1.5 h-4 w-4" /> Reenviar
                      </Button>
                      <Button size="sm" variant="destructive" disabled={busyId === g.id} onClick={() => void act(g.id, "revoke")}>
                        <Ban className="mr-1.5 h-4 w-4" /> Revocar
                      </Button>
                    </>
                  ) : g.status === "expired" ? (
                    <Button size="sm" variant="outline" disabled={busyId === g.id} onClick={() => void act(g.id, "regenerate")}>
                      <RefreshCw className="mr-1.5 h-4 w-4" /> Regenerar link
                    </Button>
                  ) : (
                    <Download className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </div>
              {expandedId === g.id ? (
                <div className="mt-4 border-t border-border/50 pt-3">
                  {eventsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin eventos registrados</p>
                  ) : (
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {events.map((e, i) => (
                        <li key={i}>
                          <span className="font-mono text-foreground">{String(e.event_type)}</span>
                          {" · "}
                          {format(parseISO(String(e.created_at)), "d MMM HH:mm", { locale: es })}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </ClinicalPageShell>
    </>
  );
}
